import React, { useRef } from "react";
import Header from "@components/header";
import InformCookiesPopup from "@components/inform_cookies_popup";
import { usePage } from "@inertiajs/react";
import "../entrypoints/i18n";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import rollbar from "@utils/rollbar.ts";
import ErrorBoundaryContext from "@contexts/error_boundary_context.ts";
import {
  queryClient,
  localStoragePersister,
  persistedQueryCacheBuster,
} from "../queries/query_client";
import { useRailsFlash, type RailsFlash } from "@/sprout/flash";
import {
  type LocationControlSettings,
  LocationControlSettingsContext,
} from "@contexts/location_control_settings_context.ts";
import { EmotionCacheProvider } from "@/sprout/emotion_cache_provider.tsx";
import IdentityVerificationConfirmationModal from "@components/IdentityVerificationConfirmationModal";
import { SubscriptionModalProvider } from "@components/job_alerts/subscription_modal.tsx";

interface PageProps {
  nonce: string;
  flash: RailsFlash;
  locationControlSettings: LocationControlSettings;
  currentUserId?: number;
  [key: string]: unknown;
}

interface LayoutProps {
  publicFacing?: boolean;
  children: React.ReactNode;
}

const ONE_DAY = 1000 * 60 * 60 * 24;

const Layout: React.FC<LayoutProps> = ({ publicFacing = false, children }: LayoutProps) => {
  const { nonce, flash, locationControlSettings, currentUserId } = usePage<PageProps>().props;

  const prevUserIdRef = useRef(currentUserId);
  if (prevUserIdRef.current !== currentUserId) {
    prevUserIdRef.current = currentUserId;
    queryClient.clear();
  }

  useRailsFlash(flash);

  return (
    <ErrorBoundaryContext.Provider value={rollbar}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: localStoragePersister,
          maxAge: ONE_DAY,
          buster: persistedQueryCacheBuster(currentUserId),
        }}
      >
        <LocationControlSettingsContext.Provider value={locationControlSettings}>
          <EmotionCacheProvider>
            <SubscriptionModalProvider>
              <div className="h-screen overflow-hidden flex flex-col">
                {!publicFacing && <Header />}
                <div
                  className="h-full overflow-auto"
                  data-provides="scroll-container"
                  /* eslint-disable-next-line react/no-unknown-property */
                  scroll-region=""
                >
                  {children}
                </div>
              </div>
            </SubscriptionModalProvider>
            <IdentityVerificationConfirmationModal />
            <InformCookiesPopup />
            <ReactQueryDevtools initialIsOpen={false} styleNonce={nonce} />
          </EmotionCacheProvider>
        </LocationControlSettingsContext.Provider>
      </PersistQueryClientProvider>
    </ErrorBoundaryContext.Provider>
  );
};

export default Layout;
